import { finalizeEvent, generateSecretKey } from "nostr-tools/pure";
import { SignalingCore } from "./core";
import type { JoinOptions, SignalingAdapter, SignalPayload, WireMessage } from "./types";

/** Ephemeral range (20000–29999): relays forward these live and never store them. */
export const NOSTR_KIND = 29333;

export interface NostrSignalingOptions {
  relays: string[];
  /** Re-announce schedule (ms): fast at first so racing joins still meet, then slower. */
  announceIntervals?: { initial: number; initialCount: number; steady: number };
  /** Reconnect backoff bounds (ms). */
  reconnect?: { min: number; max: number };
  /** Max wait for a relay subscription to go live before announcing anyway (ms). */
  subscribeTimeoutMs?: number;
  /** Injected for tests or non-browser runtimes. */
  WebSocket?: typeof WebSocket;
}

/** "limited": the relay rejected us for rate limits; we back off instead of hammering it. */
export type RelayState = "connecting" | "open" | "limited" | "closed";

interface NostrEvent {
  id: string;
  kind: number;
  tags: string[][];
  content: string;
}

const DEFAULTS = {
  announceIntervals: { initial: 3_000, initialCount: 5, steady: 20_000 },
  reconnect: { min: 1_000, max: 30_000 },
  subscribeTimeoutMs: 3_000,
};

const SEEN_EVENT_LIMIT = 2_000;
/** Back-off after a relay rejects an event as rate-limited / after it bans us. */
const RATE_LIMIT_COOLDOWN_MS = 30_000;
const BAN_COOLDOWN_MS = 10 * 60_000;
const PENDING_LIMIT = 200;

function isWireMessage(value: unknown): value is WireMessage {
  if (typeof value !== "object" || value === null) return false;
  const m = value as Record<string, unknown>;
  return (
    typeof m.from === "string" &&
    typeof m.sealed === "string" &&
    (m.to === undefined || typeof m.to === "string")
  );
}

/** One relay: connects, subscribes to the room topic, reconnects with exponential backoff. */
class RelayConnection {
  state: RelayState = "connecting";
  /** Last rejection or notice from the relay, for diagnostics. */
  lastMessage: string | undefined;
  #cooldownUntil = 0;
  #socket: WebSocket | undefined;
  #retryDelay: number;
  #retryTimer: ReturnType<typeof setTimeout> | undefined;
  #closed = false;

  constructor(
    readonly url: string,
    private readonly options: {
      WebSocket: typeof WebSocket;
      reconnect: { min: number; max: number };
      subscription: () => unknown[];
      onLive: () => void;
      onEvent: (event: NostrEvent) => void;
    },
  ) {
    this.#retryDelay = options.reconnect.min;
    this.#connect();
  }

  send(message: unknown[]): boolean {
    if (this.state === "limited" && Date.now() >= this.#cooldownUntil) this.state = "open";
    if (this.state !== "open" || !this.#socket) return false;
    try {
      this.#socket.send(JSON.stringify(message));
      return true;
    } catch {
      return false;
    }
  }

  close(): void {
    this.#closed = true;
    clearTimeout(this.#retryTimer);
    this.state = "closed";
    this.#socket?.close();
  }

  #connect(): void {
    if (this.#closed) return;
    this.state = "connecting";
    let socket: WebSocket;
    try {
      socket = new this.options.WebSocket(this.url);
    } catch {
      this.#scheduleReconnect();
      return;
    }
    this.#socket = socket;
    socket.onopen = () => {
      this.state = "open";
      this.#retryDelay = this.options.reconnect.min;
      this.send(this.options.subscription());
    };
    socket.onmessage = (message) => this.#handle(String(message.data));
    socket.onclose = () => {
      if (this.#socket !== socket) return;
      this.state = this.#closed ? "closed" : "connecting";
      this.#scheduleReconnect();
    };
    socket.onerror = () => socket.close();
  }

  #scheduleReconnect(): void {
    if (this.#closed) return;
    clearTimeout(this.#retryTimer);
    const jitter = 0.5 + Math.random();
    this.#retryTimer = setTimeout(() => this.#connect(), this.#retryDelay * jitter);
    this.#retryDelay = Math.min(this.#retryDelay * 2, this.options.reconnect.max);
  }

  /** NIP-01 machine-readable prefixes: back off on "rate-limited:" and "banned:". */
  #onRejected(reason: string): void {
    this.lastMessage = reason;
    const cooldown = reason.startsWith("banned")
      ? BAN_COOLDOWN_MS
      : reason.startsWith("rate-limited")
        ? RATE_LIMIT_COOLDOWN_MS
        : 0;
    if (cooldown === 0 || this.state !== "open") return;
    this.state = "limited";
    this.#cooldownUntil = Date.now() + cooldown;
  }

  #handle(raw: string): void {
    let message: unknown;
    try {
      message = JSON.parse(raw);
    } catch {
      return;
    }
    if (!Array.isArray(message)) return;
    if (message[0] === "EOSE") this.options.onLive();
    else if (message[0] === "OK" && message[2] === false)
      this.#onRejected(String(message[3] ?? ""));
    else if (message[0] === "NOTICE") this.lastMessage = String(message[1] ?? "");
    else if (message[0] === "EVENT" && typeof message[2] === "object" && message[2] !== null) {
      this.options.onEvent(message[2] as NostrEvent);
    }
  }
}

/**
 * Signaling over public Nostr relays (docs/PRD.md §2). Each session uses a throwaway keypair and
 * publishes ephemeral events tagged with the room topic; content is the sealed `WireMessage`, so
 * relays learn nothing but the topic hash. Every relay is used at once; any one being up suffices.
 */
export class NostrSignaling implements SignalingAdapter {
  readonly kind = "nostr";
  readonly #options: Required<Omit<NostrSignalingOptions, "WebSocket">> & {
    WebSocket: typeof WebSocket;
  };
  readonly #core: SignalingCore;
  #relays: RelayConnection[] = [];
  #secretKey: Uint8Array | undefined;
  readonly #seenEvents = new Set<string>();
  #pending: unknown[][] = [];
  #announceTimer: ReturnType<typeof setTimeout> | undefined;
  #announcesSent = 0;
  #live = false;
  #liveWaiters: (() => void)[] = [];

  constructor(options: NostrSignalingOptions) {
    const WebSocketImpl = options.WebSocket ?? globalThis.WebSocket;
    if (!WebSocketImpl) throw new Error("nostr signaling needs WebSocket");
    this.#options = { ...DEFAULTS, ...options, WebSocket: WebSocketImpl };
    this.#core = new SignalingCore((message) => this.#publish(message));
  }

  /** Per-relay connection state, for diagnostics. */
  relayStates(): { url: string; state: RelayState; lastMessage?: string }[] {
    return this.#relays.map((r) =>
      r.lastMessage === undefined
        ? { url: r.url, state: r.state }
        : { url: r.url, state: r.state, lastMessage: r.lastMessage },
    );
  }

  async join(options: JoinOptions): Promise<void> {
    await this.leave();
    this.#core.start(options);
    this.#secretKey = generateSecretKey();
    const subscription = () => [
      "REQ",
      `cs-${options.topic.slice(0, 16)}`,
      { kinds: [NOSTR_KIND], "#t": [options.topic], since: Math.floor(Date.now() / 1000) - 30 },
    ];
    this.#relays = this.#options.relays.map(
      (url) =>
        new RelayConnection(url, {
          WebSocket: this.#options.WebSocket,
          reconnect: this.#options.reconnect,
          subscription,
          onLive: () => this.#onRelayLive(),
          onEvent: (event) => this.#onEvent(event, options.topic),
        }),
    );
    // Announce once a subscription is live (so replies can reach us), or after a timeout.
    await Promise.race([
      new Promise<void>((resolve) => this.#liveWaiters.push(resolve)),
      new Promise((resolve) => setTimeout(resolve, this.#options.subscribeTimeoutMs)),
    ]);
    if (this.#core.joined !== options) return;
    await this.#core.announce().catch(() => {});
    this.#scheduleAnnounce();
  }

  onPeerSeen(callback: (peerId: string) => void) {
    return this.#core.onPeerSeen(callback);
  }

  onSignal(callback: (from: string, payload: SignalPayload) => void) {
    return this.#core.onSignal(callback);
  }

  send(to: string, payload: SignalPayload): Promise<void> {
    return this.#core.send(to, payload);
  }

  forget(peerId: string): void {
    this.#core.forget(peerId);
  }

  async leave(): Promise<void> {
    clearTimeout(this.#announceTimer);
    for (const relay of this.#relays) relay.close();
    this.#relays = [];
    this.#pending = [];
    this.#seenEvents.clear();
    this.#announcesSent = 0;
    this.#live = false;
    this.#core.stop();
  }

  #onRelayLive(): void {
    const firstLive = !this.#live;
    this.#live = true;
    for (const resolve of this.#liveWaiters.splice(0)) resolve();
    this.#flushPending();
    // A relay that (re)connected after we joined missed our announce.
    if (!firstLive && this.#core.joined) void this.#core.announce().catch(() => {});
  }

  #onEvent(event: NostrEvent, topic: string): void {
    if (event.kind !== NOSTR_KIND || !event.tags.some(([k, v]) => k === "t" && v === topic)) return;
    if (this.#seenEvents.has(event.id)) return; // the same event via several relays
    this.#seenEvents.add(event.id);
    if (this.#seenEvents.size > SEEN_EVENT_LIMIT) {
      this.#seenEvents.delete(this.#seenEvents.values().next().value as string);
    }
    let message: unknown;
    try {
      message = JSON.parse(event.content);
    } catch {
      return;
    }
    if (isWireMessage(message)) void this.#core.receive(message);
  }

  async #publish(message: WireMessage): Promise<void> {
    const joined = this.#core.joined;
    if (!joined || !this.#secretKey) throw new Error("signaling: not joined");
    const now = Math.floor(Date.now() / 1000);
    const event = finalizeEvent(
      {
        kind: NOSTR_KIND,
        created_at: now,
        tags: [
          ["t", joined.topic],
          ["expiration", String(now + 120)], // NIP-40: relays may drop it after two minutes
        ],
        content: JSON.stringify(message),
      },
      this.#secretKey,
    );
    const frame = ["EVENT", event];
    const sent = this.#relays.map((relay) => relay.send(frame)).some(Boolean);
    if (!sent) {
      // No relay is up right now: hold it (bounded) until one reconnects.
      this.#pending.push(frame);
      if (this.#pending.length > PENDING_LIMIT) this.#pending.shift();
    }
  }

  #flushPending(): void {
    const pending = this.#pending.splice(0);
    for (const frame of pending) {
      if (!this.#relays.map((relay) => relay.send(frame)).some(Boolean)) this.#pending.push(frame);
    }
  }

  #scheduleAnnounce(): void {
    const { initial, initialCount, steady } = this.#options.announceIntervals;
    const delay = this.#announcesSent < initialCount ? initial : steady;
    this.#announceTimer = setTimeout(() => {
      this.#announcesSent++;
      void this.#core.announce().catch(() => {});
      this.#scheduleAnnounce();
    }, delay);
  }
}
